// v10/src/components/admin/fuel/FuelTab.js
//
// Pestaña Combustible del módulo "Mantenimiento e inventario".
// Tarea 2: integra los modales de tanques y movimientos + tabla de movimientos.
// Polling unificado: cuando isActive=false, pausa el polling de tanques Y de la tabla.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Fuel, Plus, Truck, SlidersHorizontal,
  RefreshCw, AlertCircle, Loader, FileDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fuelService from '../../../services/fuelService';
import { reportsService } from '../../../services/reportsService';
import { POLLING_INTERVALS } from '../../../config/realtime';
import useIsMobile from '../../../hooks/useIsMobile';
import FuelTankCard           from './FuelTankCard';
import TankFormModal          from './TankFormModal';
import IntakeModal            from './IntakeModal';
import DispatchModal          from './DispatchModal';
import AdjustmentModal        from './AdjustmentModal';
import DeleteTankConfirmModal from './DeleteTankConfirmModal';
import FuelMovementsTable     from './FuelMovementsTable';
import ExportReportModal      from '../../common/ExportReportModal';

// Roles que pueden gestionar tanques y movimientos.
const MANAGE_ROLES = new Set(['admin', 'instructor', 'superadmin']);

const FuelTab = ({ darkMode, userRole, locale = 'es-AR', onTanksChange, isActive = true }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();
  const [tanks,   setTanks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const pollRef = useRef(null);

  // Estado de modales: { type, tank? }
  const [modal, setModal] = useState(null);

  // Refresh nonce para forzar reload de la tabla cuando se completa una acción.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Modal de exportación de reporte (PDF/Excel)
  const [exportOpen, setExportOpen] = useState(false);

  const canManage = MANAGE_ROLES.has(userRole);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fuelService.getTanks();
      const data = res?.data?.data || [];
      setTanks(data);
      if (onTanksChange) onTanksChange(data);
    } catch (err) {
      setError(t('fuel_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t, onTanksChange]);

  // Carga inicial
  useEffect(() => { load(); }, [load]);

  // Polling — pausado si la pestaña no está activa
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive) return undefined;

    pollRef.current = setInterval(load, POLLING_INTERVALS.maintenance);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, load]);

  const closeModal = useCallback(() => setModal(null), []);

  const handleSuccess = useCallback(() => {
    load();
    setRefreshNonce((n) => n + 1);
  }, [load]);

  const openCreate     = () => setModal({ type: 'create' });
  const openEdit       = (tank) => setModal({ type: 'edit', tank });
  const openDelete     = (tank) => setModal({ type: 'delete', tank });
  const openIntake     = () => setModal({ type: 'intake' });
  const openDispatch   = () => setModal({ type: 'dispatch' });
  const openAdjustment = () => setModal({ type: 'adjustment' });

  // Estilos compartidos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  const actionBtn = (variant) => {
    const variants = {
      primary: {
        background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
        color: 'white',
        boxShadow: '0 6px 16px rgba(79,70,229,0.28)',
      },
      secondary: {
        background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
        color: darkMode ? 'rgba(255,255,255,0.85)' : '#475569',
      },
    };
    return {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
      transition: 'transform 0.15s',
      ...(isMobile ? { width: '100%', justifyContent: 'center' } : null),
      ...variants[variant],
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Toolbar de acciones — IZQ: operativas | DER: refresh + configuración */}
      <div
        style={isMobile ? {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
        } : {
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Izquierda — acciones operativas frecuentes */}
        <div
          style={isMobile ? {
            display: 'contents',
          } : {
            display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
          }}
        >
          {canManage && (
            <>
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={openIntake}
                disabled={tanks.length === 0}
                style={{
                  ...actionBtn('secondary'),
                  opacity: tanks.length === 0 ? 0.5 : 1,
                  cursor: tanks.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Fuel style={{ width: 15, height: 15 }} />
                {t('fuel_action_load')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={openDispatch}
                disabled={tanks.length === 0}
                style={{
                  ...actionBtn('secondary'),
                  opacity: tanks.length === 0 ? 0.5 : 1,
                  cursor: tanks.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Truck style={{ width: 15, height: 15 }} />
                {t('fuel_action_dispatch')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={openAdjustment}
                disabled={tanks.length === 0}
                style={{
                  ...actionBtn('secondary'),
                  opacity: tanks.length === 0 ? 0.5 : 1,
                  cursor: tanks.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <SlidersHorizontal style={{ width: 15, height: 15 }} />
                {t('fuel_action_adjust')}
              </motion.button>
            </>
          )}
        </div>

        {/* Derecha — refresh + acciones de configuración */}
        <div
          style={isMobile ? {
            display: 'contents',
          } : {
            display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
          }}
        >
          <motion.button
            onClick={() => { load(); setRefreshNonce((n) => n + 1); }}
            whileTap={{ scale: 0.96 }}
            aria-label="refresh"
            style={{
              padding: 10, borderRadius: 12, border: 'none', cursor: 'pointer',
              background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
              color: fgMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...(isMobile ? { width: '100%' } : null),
            }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} />
          </motion.button>

          {canManage && (
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => setExportOpen(true)}
              style={actionBtn('secondary')}
            >
              <FileDown style={{ width: 15, height: 15 }} />
              {t('report_button_export')}
            </motion.button>
          )}

          {canManage && (
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={openCreate}
              style={actionBtn('primary')}
            >
              <Plus style={{ width: 15, height: 15 }} />
              {t('fuel_action_new_tank')}
            </motion.button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 14,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            color: '#dc2626',
            fontSize: 13, fontWeight: 600,
          }}
        >
          <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading inicial */}
      {loading && tanks.length === 0 && !error && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            minHeight: 220, color: fgMuted, fontSize: 14,
          }}
        >
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
          <Loader style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
          {t('fuel_loading')}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && tanks.length === 0 && (
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 14, padding: '48px 24px',
            borderRadius: 20,
            border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
            background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56, height: 56, borderRadius: 18,
              background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Fuel style={{ width: 26, height: 26, color: '#6366f1' }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>
              {t('fuel_empty_title')}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 420 }}>
              {t('fuel_empty_desc')}
            </p>
          </div>
          {canManage && (
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={openCreate}
              style={actionBtn('primary')}
            >
              <Plus style={{ width: 15, height: 15 }} />
              {t('fuel_empty_cta')}
            </motion.button>
          )}
        </div>
      )}

      {/* Grid de cards */}
      {tanks.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
          }}
        >
          {tanks.map((tank) => (
            <FuelTankCard
              key={tank.id}
              tank={tank}
              darkMode={darkMode}
              canManage={canManage}
              onEdit={openEdit}
              onDelete={openDelete}
              locale={locale}
            />
          ))}
        </div>
      )}

      {/* Tabla de movimientos */}
      {tanks.length > 0 && (
        <FuelMovementsTable
          tanks={tanks}
          darkMode={darkMode}
          locale={locale}
          isActive={isActive}
          refreshNonce={refreshNonce}
        />
      )}

      {/* Modales */}
      <AnimatePresence>
        {modal?.type === 'create' && (
          <TankFormModal
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
          />
        )}
        {modal?.type === 'edit' && modal.tank && (
          <TankFormModal
            tank={modal.tank}
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
          />
        )}
        {modal?.type === 'delete' && modal.tank && (
          <DeleteTankConfirmModal
            tank={modal.tank}
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
          />
        )}
        {modal?.type === 'intake' && (
          <IntakeModal
            tanks={tanks}
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
            locale={locale}
          />
        )}
        {modal?.type === 'dispatch' && (
          <DispatchModal
            tanks={tanks}
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
            locale={locale}
          />
        )}
        {modal?.type === 'adjustment' && (
          <AdjustmentModal
            tanks={tanks}
            onSuccess={handleSuccess}
            onClose={closeModal}
            darkMode={darkMode}
            locale={locale}
          />
        )}

        {exportOpen && (
          <ExportReportModal
            isOpen={exportOpen}
            title={t('report_modal_title_fuel')}
            formats={['pdf', 'xlsx']}
            periodOptions={{ monthPicker: true, rangePicker: true }}
            onExport={(params) => reportsService.fuelConsumption(params)}
            onClose={() => setExportOpen(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FuelTab;
